import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import { loadCategoriesByProcess, loadDeploymentData } from "@/store/deploy/actions";
import { changeCategory, copyDefinition, deleteProcess, downloadProcess, loadCategories, loadProcessDefinition } from "@/store/flow/actions";
import { downloadFile } from "@/utils/file-download";
import SdkFlowDeleteDefinition from "./dialogs/sdk-flow-delete-definition/sdk-flow-delete-definition.vue";
import SdkFlowDefinitionHistory from "./dialogs/sdk-flow-definition-history/sdk-flow-definition-history.vue";
import SdkFlowCopyDefinition from "./dialogs/sdk-flow-copy-definition/sdk-flow-copy-definition.vue";
import { EventBus } from "@/utils/event-bus";
import { ETypeNotification } from "@/interfaces/i-notification";
import { IDeploymentInfo } from "@/interfaces/sdk-flow";
import { generateBpmnId } from "@/utils/generateid";
import { tokenInfo } from "@/store/login/types";
import { loadEnvironmentsByRole } from "@/store/environment/actions";
import { EnvironmentListResponse } from "@/interfaces/sdk-environment";

export default defineComponent({
  name: "sdk-flow-instance-viewer",
  components: {
    "sdk-flow-bpmn-viewer": () => import("../sdk-flow-bpmn-viewer/sdk-flow-bpmn-viewer.vue"),
    "sdk-bpmn-action-deploy": () => import("./actions/sdk-bpmn-action-deploy/sdk-bpmn-action-deploy.vue"),
    SdkFlowDeleteDefinition,
    SdkFlowDefinitionHistory,
    SdkFlowCopyDefinition,
  },
  data() {
    return {
      definition: {} as any,
      categories: [] as any[],
      processCategory: {} as any,
      processName: "",
      categoryId: 0,
      processDescription: "",
      showDeleteDialog: false,
      isLoading: false,
      showHistoryDialog: false,
      lastDeployInfo: {} as IDeploymentInfo,
      fetchingEnvironments: true,
      availableEnvironments: [] as EnvironmentListResponse[],
      showCopyDialog: false,
      copyingToEnvironment: false,
    };
  },
  computed: {
    ...mapGetters({
      tokenInfo,
    }),
    currentCategoryProps(): { name: string; description: string } {
      const category: any = this.categories.filter((cat: any) => cat.id === Number(this.categoryId))[0];
      return { name: category.name, description: category.description };
    },
  },
  methods: {
    changeDefinitionCategory(categoryId: number) {
      this.$dispatch(changeCategory)([categoryId, this.definition.key], this.$t("flow.instanceViewer.notifications.move.success"));
    },
    copyDefinitionToCategory(categoryId: number | null, environmentId?: number, targetCategoryName?: string) {
      if (categoryId !== null) {
        this.copyingToEnvironment = true;
        const targetEnvironment = typeof environmentId === "number" ? environmentId : this.tokenInfo.environment.id;
        const newId = generateBpmnId();
        const category = this.categories.find((cat: any) => cat.id === categoryId);
        const successMessage = (this.$t("flow.instanceViewer.notifications.copyProcess.success") as string).replace(
          "{0}",
          category ? category.name : targetCategoryName
        );
        this.$dispatch(copyDefinition)([this.$route.params.definitionKey, newId, categoryId, targetEnvironment], successMessage).finally(() => {
          this.copyingToEnvironment = false;
        });
      } else {
        this.toggleCopyDialog();
      }
    },
    clearSelectedTask() {
      const node = document.querySelector(".selected-task") as HTMLElement | null;
      if (node) {
        node.classList.remove("selected-task");
      }
    },
    returnButton() {
      this.$router.push({
        name: "flow-category",
        params: {
          categoryId: this.categoryId.toString(),
          categoryName: this.currentCategoryProps.name,
          categoryDescription: this.currentCategoryProps.description,
        },
      });
    },
    openDeleteDialog() {
      this.showDeleteDialog = true;
    },
    closeDeleteDialog() {
      this.showDeleteDialog = false;
    },
    deleteProcess() {
      this.$dispatch(deleteProcess)([this.definition.id, this.tokenInfo.environment.id]).then(() => {
        this.returnButton();
      });
    },
    async downloadDefinition() {
      await this.$dispatch(downloadProcess)([this.definition.id]).then((res: any) => {
        downloadFile(this.definition.name, res, "bpmn");
      });
    },
    toggleDeleteDialog(value?: boolean) {
      if (typeof value === "boolean") {
        this.showDeleteDialog = value;
        return;
      }

      this.showDeleteDialog = !this.showDeleteDialog;
    },
    toggleCopyDialog() {
      this.showCopyDialog = !this.showCopyDialog;
    },
    toggleHistoryDialog(value?: boolean) {
      if (typeof value === "boolean") {
        this.showHistoryDialog = value;
        return;
      }

      this.showHistoryDialog = !this.showHistoryDialog;
    },
    copyIdClipboard(inputId: string) {
      const testingCodeToCopy = document.querySelector(`#${inputId}`) as any;
      testingCodeToCopy.setAttribute("type", "text");
      testingCodeToCopy.select();

      try {
        document.execCommand("copy");
      } catch (err) {
        EventBus.$emit("emmit-notification", {
          title: this.$t("flow.instanceViewer.notifications.copy.error.title"),
          message: this.$t("flow.instanceViewer.notifications.copy.error.message"),
          type: ETypeNotification.DANGER,
        });
      }
      testingCodeToCopy.setAttribute("type", "hidden");
      (window.getSelection() as Selection).removeAllRanges();
    },
    getHistoryHint() {
      return this.definition.version === 1 ? this.$t("flow.instanceViewer.buttons.hints.history") : "";
    },
    async getDeployInfo() {
      this.lastDeployInfo = await this.$dispatch(loadDeploymentData)([this.definition.key]);
      return;
    },
    getEnvironmentList() {
      this.$dispatch(loadEnvironmentsByRole)(["IMPORTAR_PROCESSO"])
        .then((data: EnvironmentListResponse[]) => {
          this.availableEnvironments = data;
        })
        .finally(() => {
          this.fetchingEnvironments = false;
        });
    },
    async loadDependencies() {
      this.isLoading = true;
      this.getEnvironmentList();
      this.definition = await this.$dispatch(loadProcessDefinition)([this.$route.params.definitionId]);
      this.categories = await this.$dispatch(loadCategories)();
      this.processCategory = await this.$dispatch(loadCategoriesByProcess)([this.definition.key]);
      await this.getDeployInfo();
      this.isLoading = false;
      this.processName = `${this.definition.name}-${this.$generateId(4)}`;
      this.categoryId = Number(this.$route.params.categoryId);
    },
  },
  mounted() {
    this.loadDependencies();
  },
});
